import { buildCustomerDefaultContact, isDefined, normalizeCountry } from '@klareospace/pharos-lib-utils';
import { BARCODE_TYPES, TRANSPORT_TYPE } from '@klareospace/pharos-lib-types';
import type {
  Contact,
  CreateDelivery,
  CreateDeliveryAddress,
  CreateHandlingUnit,
  CreateOrder,
  CreatePickup,
  Customer,
  CustomerContact,
  GeoCoordinates,
  GeoPoint,
  OrderType,
  Site,
} from '@klareospace/pharos-lib-types';
import type { DateRangeValue } from '@klareospace/pharos-lib-web-utils';

import { buildAddress, buildOverrideContact } from '&/features/customers';
import type { AddressBookEntryInput } from '&/features/customers';

import type {
  ContentHandlingUnitLineFormInput,
  OrderCreationFormInput,
  RouteInformationFormInput,
} from '../schemas/order-creation.schema';

// ─── Constants ────────────────────────────────────────────────────────────────

/** Optional `CreateDeliveryAddress` string fields — blank values are dropped, not sent as `''`. */
const OPTIONAL_DELIVERY_ADDRESS_STRING_FIELDS = [
  'additionalInformations',
  'city',
  'complement',
  'digicode',
  'floor',
  'houseNumber',
  'postalCode',
  'street',
] as const;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface BuildCreateOrderBodyParams {
  orderTypes: OrderType[];
}

// ─── Builder ──────────────────────────────────────────────────────────────────

/**
 * Maps the fully-validated manual order creation form to the `POST /orders` contract. Pure — the
 * caller (`useSubmitOrder`) is responsible for re-validating every section first; `requireCustomer`,
 * `requireSite`, `requireDeliveryTimeSlot`, `requireShipperCode`, and `resolveOrderTypeCode` below
 * throw only for fields the `CreateOrder` contract has no optional variant for (customer, pickup
 * site, delivery timeslot, shipper, order type), which a validated form always carries.
 */
export function buildCreateOrderBody(
  form: OrderCreationFormInput,
  { orderTypes }: BuildCreateOrderBodyParams,
): CreateOrder {
  const { content, customer, generalInformation, routeInformation, scheduling } = form;
  const selectedCustomer = requireCustomer(customer.selectedCustomer);
  const selectedSite = requireSite(routeInformation.selectedSite);
  const deliveryTimeSlot = requireDeliveryTimeSlot(scheduling.deliveryTimeSlot);
  const shipperCode = requireShipperCode(generalInformation.shipperCode);

  const body: CreateOrder = {
    customer: buildCustomerContact(selectedCustomer),
    delivery: buildDelivery(routeInformation, deliveryTimeSlot, selectedCustomer),
    orderTypeCode: resolveOrderTypeCode(orderTypes, generalInformation.orderTypeId),
    pickup: buildPickup(routeInformation, selectedSite),
    requestedTimeWindow: { end: deliveryTimeSlot.range.to, start: deliveryTimeSlot.range.from },
    shipperCode,
    siteCode: selectedSite.code,
    tags: content.tags,
    transportType: TRANSPORT_TYPE.DELIVERY,
  };

  const referenceNumber = generalInformation.referenceNumber.trim();
  if (referenceNumber) body.referenceNumber = referenceNumber;
  if (scheduling.carrierCode) body.requestedCarrierCode = scheduling.carrierCode;

  const handlingUnits = buildHandlingUnits(content.handlingUnits);
  if (handlingUnits) body.handlingUnits = handlingUnits;

  const totalHandlingUnitQuantity = parsePositiveNumber(content.totalQuantity);
  if (isDefined(totalHandlingUnitQuantity)) body.totalHandlingUnitQuantity = totalHandlingUnitQuantity;

  const totalWeight = parsePositiveNumber(content.totalWeight);
  if (isDefined(totalWeight)) body.totalWeight = { unit: content.weightUnit, value: totalWeight };

  const totalVolume = parsePositiveNumber(content.totalVolume);
  if (isDefined(totalVolume)) body.totalVolume = { unit: content.volumeUnit, value: totalVolume };

  const orderValue = parsePositiveNumber(content.orderValue);
  if (isDefined(orderValue)) body.orderValue = { currency: content.currency, value: orderValue };

  return body;
}

// ─── Helpers (private) ──────────────────────────────────────────────────────────

function requireCustomer(customer: Customer | null): Customer {
  if (!customer) throw new Error('[buildCreateOrderBody] No customer selected');
  return customer;
}

function requireSite(site: Site | null): Site {
  if (!site) throw new Error('[buildCreateOrderBody] No pickup site selected');
  return site;
}

function requireDeliveryTimeSlot(value: DateRangeValue | null): DateRangeValue {
  if (!value) throw new Error('[buildCreateOrderBody] No delivery timeslot set');
  return value;
}

function requireShipperCode(shipperCode: string): string {
  if (!shipperCode) throw new Error('[buildCreateOrderBody] No shipper selected');
  return shipperCode;
}

function resolveOrderTypeCode(orderTypes: OrderType[], orderTypeId: string): string {
  const orderType = orderTypes.find((type) => type.id === orderTypeId);
  if (!orderType) {
    throw new Error(`[buildCreateOrderBody] No order type found for id '${orderTypeId}'`);
  }
  return orderType.code;
}

function buildCustomerContact(customer: Customer): CustomerContact {
  const contact: CustomerContact = {
    firstName: customer.firstName,
    lastName: customer.lastName,
    phone: customer.phone,
  };
  if (customer.email) contact.email = customer.email;
  return contact;
}

function buildPickup(route: RouteInformationFormInput, site: Site): CreatePickup {
  const pickup: CreatePickup = { durationTime: route.pickupDurationTime, siteCode: site.code };
  if (route.pickupTimeSlot.enabled && route.pickupTimeSlot.value) {
    pickup.timeSlot = { end: route.pickupTimeSlot.value.range.to, start: route.pickupTimeSlot.value.range.from };
  }
  return pickup;
}

/**
 * `delivery.siteCode` is deliberately never set — the order's pickup site is the only site
 * reference. `delivery.timeSlot` mirrors `requestedTimeWindow` — the server's create path does not
 * derive it on its own (only the update path does, via `order-patch-builders.ts`), while the
 * KLAREO transformer always sends both from the same source value (see `klareo.transformer.ts`).
 */
function buildDelivery(
  route: RouteInformationFormInput,
  deliveryTimeSlot: DateRangeValue,
  customer: Customer,
): CreateDelivery {
  const delivery: CreateDelivery = {
    durationTime: route.deliveryDurationTime,
    timeSlot: { end: deliveryTimeSlot.range.to, start: deliveryTimeSlot.range.from },
  };
  if (!route.deliveryAddress) return delivery;

  const addressFields = buildDeliveryAddressFields(route.deliveryAddress);
  if (addressFields) delivery.address = addressFields;

  const contact = buildDeliveryContact(route.deliveryAddress, customer);
  if (contact) delivery.contact = contact;

  return delivery;
}

/**
 * Explicit over omission: the server resolves `delivery.contact` as payload > stored address
 * override > customer default, so omitting it here would let a stale stored override win. When the
 * entry marks the customer as the contact, the customer's own default contact is sent explicitly;
 * otherwise the entry's override is sent (or `undefined` when the override itself is incomplete).
 * Distinct from the display-only `resolveDeliveryContact` (`route-information.utils.ts`), which
 * independently falls back per name/phone field for UI rendering rather than the server contract.
 */
function buildDeliveryContact(entry: AddressBookEntryInput, customer: Customer): Contact | undefined {
  if (entry.isCustomerContact) return buildCustomerDefaultContact(customer);
  return buildOverrideContact(entry);
}

function buildDeliveryAddressFields(entry: AddressBookEntryInput): CreateDeliveryAddress | undefined {
  const address = buildAddress(entry);
  const fields: CreateDeliveryAddress = {};

  for (const key of OPTIONAL_DELIVERY_ADDRESS_STRING_FIELDS) {
    const value = address[key]?.trim();
    if (value) fields[key] = value;
  }
  if (address.country) fields.country = normalizeCountry(address.country);

  const coordinates = buildCoordinates(entry.coordinates);
  if (coordinates) fields.coordinates = coordinates;

  // Nothing meaningful was entered — an untouched entry's `hasElevator: false` default must not
  // make an otherwise-blank address look "provided".
  if (Object.keys(fields).length === 0) return undefined;

  if (isDefined(address.hasElevator)) fields.hasElevator = address.hasElevator;
  return fields;
}

function buildCoordinates(coordinates: Partial<GeoCoordinates> | undefined): GeoPoint | undefined {
  if (!coordinates || !isDefined(coordinates.latitude) || !isDefined(coordinates.longitude)) return undefined;
  return { latitude: coordinates.latitude, longitude: coordinates.longitude };
}

function buildHandlingUnits(lines: ContentHandlingUnitLineFormInput[]): CreateHandlingUnit[] | undefined {
  if (lines.length === 0) return undefined;
  return lines.map(buildHandlingUnit);
}

function buildHandlingUnit(line: ContentHandlingUnitLineFormInput, sortIndex: number): CreateHandlingUnit {
  const unit: CreateHandlingUnit = {
    quantity: line.quantity,
    sortIndex,
    temperature: line.temperature,
    type: line.typeCode,
  };
  const barcode = line.barcode.trim();
  if (barcode) unit.code = { type: BARCODE_TYPES.SSCC, value: barcode };
  return unit;
}

/** Parses a validated numeric form field (already guaranteed positive-or-empty by the zod schema); blank ⇒ undefined. */
function parsePositiveNumber(value: string): number | undefined {
  const trimmed = value.trim();
  return trimmed ? Number(trimmed) : undefined;
}
